import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PLAYBACKS } from '@hotjar/webapp/constants/routes';
import { getPageType, getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';
import { usePrevious } from '@hotjar/webapp/hooks/usePrevious';
import { refetchRecordingsList, bus } from '@hotjar/webapp/utils/events';
import { useSetNotificationSent } from './useFirstRecordingStatus';
import { useFirstRecordingStatusWithPolling } from './useFirstRecordingStatusWithPolling';

interface UseRecordingsPopup {
  dismiss: (isCTAClick?: boolean) => void;
  isTriggered: boolean;
  isVisible: boolean;
}

const getTrackingData = (currentSite?: Site) => ({
  account_id: currentSite?.account?.id,
  organization_id: currentSite?.organization?.id,
  site_id: currentSite?.id,
});

export const useRecordingsPopup = (): UseRecordingsPopup => {
  const location = useLocation();
  const isInRecordingsPage = [PLAYBACKS.LIST.name].includes(
    getRouteConfigByPath(location.pathname)?.name || '',
  );
  const pageType = getPageType(location.pathname ?? '');
  const isFeaturePage = pageType === 'feature';

  const { mutate: setNotificationSent } = useSetNotificationSent();
  const { click: trackClick, viewElement } = useTrack();
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const { firstRecordingStatus, isLoading } =
    useFirstRecordingStatusWithPolling(isInRecordingsPage);

  const prevFirstRecordingStatus = usePrevious(firstRecordingStatus);
  const trackingData = getTrackingData(currentSite);

  // used on legacy popup
  const isTriggered =
    !!firstRecordingStatus?.first_recording_present && !firstRecordingStatus.notification_sent;
  const isVisible = isTriggered && !isLoading && !isInRecordingsPage && !!isFeaturePage;

  const dismiss = useCallback(
    (isCTAClick?: boolean) => {
      if (currentSite && currentUser) {
        trackClick(isCTAClick ? 'CTA' : 'Close', 'First Recording Popup', trackingData);
        setNotificationSent({ site: currentSite, user: currentUser });
      }
    },
    [setNotificationSent, currentSite, currentUser, trackClick, trackingData],
  );

  // when the popup turns on we track it
  useEffect(() => {
    if (isVisible) {
      viewElement('First Recording Popup', 'First Recording Popup', trackingData);
    }
  }, [isVisible, viewElement, trackingData]);

  // if user receives a recording while on listing page
  // we call onClick to trigger recordings refetch
  useEffect(() => {
    if (
      currentSite &&
      currentUser &&
      isInRecordingsPage &&
      prevFirstRecordingStatus?.first_recording_present === false &&
      firstRecordingStatus?.first_recording_present
    ) {
      viewElement('Auto Refresh Recordings List', 'First Recording Popup', trackingData);
      setNotificationSent({ site: currentSite, user: currentUser });
      bus.publish(refetchRecordingsList());
    }
  }, [
    currentSite,
    currentUser,
    firstRecordingStatus,
    isInRecordingsPage,
    prevFirstRecordingStatus,
    trackingData,
    setNotificationSent,
    viewElement,
  ]);

  // if user navigates to recordings page through side bar
  // or loads the recordings list page without having dismissed
  // the popup previously
  useEffect(() => {
    if (
      currentSite &&
      currentUser &&
      isInRecordingsPage &&
      firstRecordingStatus?.first_recording_present &&
      !firstRecordingStatus?.notification_sent
    ) {
      setNotificationSent({ site: currentSite, user: currentUser });
    }
  }, [currentSite, currentUser, isInRecordingsPage, firstRecordingStatus, setNotificationSent]);

  return {
    isVisible,
    isTriggered,
    dismiss,
  };
};
